import React, { useEffect, useState, useContext } from "react";
// FullCalendarコンポーネント。
import FullCalendar, { DatesSetArg, EventContentArg } from "@fullcalendar/react";
// FullCalendarで週表示を可能にするモジュール。
import timeGridPlugin from "@fullcalendar/timegrid";
// FullCalendarで月表示を可能にするモジュール。
import dayGridPlugin from "@fullcalendar/daygrid";
// FullCalendarでリスト表示を可能にするモジュール。
import listPlugin from "@fullcalendar/list";
// FullCalendarで日付や時間が選択できるようになるモジュール。
import interactionPlugin from "@fullcalendar/interaction";
import { registerLocale } from "react-datepicker";
import ja from "date-fns/locale/ja";
import { IconButton, Grid, Button } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { InfoOutlined as InfoOutlinedIcon } from "@material-ui/icons";
import { Loading } from "pages/parts/Loading";
import { SolventsInfoByBookingTypeDialog } from "pages/booking/SolventsInfoByBookingTypeDialog";
import { ConfirmModal } from "pages/parts/basicSettings/ConfirmModal";

import { SolventApi } from "api/SolventApi";
import { BookableSolventApi } from "api/BookableSolventApi";
import { BookingApi } from "api/BookingApi";
import { MailApi } from "api/MailApi";
import { UserApi } from "api/UserApi";
import { Booking } from "api/entities/Booking";
import { User } from "api/entities/User";
import { EventType } from "pages/statuses/EventType";
import { BookingDetail } from "pages/statuses/BookingDetail";
import { Solvent } from "pages/statuses/Solvent";
import { Mail } from "pages/statuses/Mail";
import { SubscribeDialog } from "pages/booking/SubscribeDialog";
import { DateUtil } from "utilities/DateUtil";
import { ConfigGetter } from "utilities/ConfigGetter";
import * as ConfigKey from "utilities/ConfigGetter";
import * as CommonConstants from "utilities/CommonConstants";
import * as DbValueConstants from "utilities/DbValueConstants";
import { SelectBookingChangeOrDeleteDialog } from "pages/booking/SelectBookingChangeOrDeleteDialog";
import { ConfirmDialog } from "pages/booking/ConfirmDialog";
import { SigninContext } from "pages/signin/SigninContext";
import { BookingUtil } from "pages/booking/BookingUtil";
import { BookingTypeView } from "pages/booking/BookingTypeView";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        buttons: {
            "& > *": {
                margin: theme.spacing(1),
            },
        },
        holiday: {
            backgroundColor: `#F3F3F3`,
        },
        bookable_days: {
            backgroundColor: `#dae3f3`,
        },
        // リスト表示のテーブル幅
        listTable_tdSubscriberName: {
            width: `200px`,
        },
        listTable_tdLaboratory: {
            width: `300px`,
        },
        listTable_tdBookingType: {
            width: `100px`,
        },
        listTable_tdSolventName: {
            width: `300px`,
        },
        listTable_th: {
            fontSize: `x-small`,
            border: `none !important`,
            textAlign: `left !important` as any,
        },
        listTable_td: {
            fontSize: `small`,
            border: `none !important`,
        },
        loading: {
            width: `20%`,
            margin: `0 auto`,
            marginTop: 10,
            position: `absolute`,
            top: `50%`,
            left: `50%`,
        },
        root: {
            width: `100%`,
            position: `relative`,
        },
        cautions_select_pc: {
            textAlign: `center`,
            fontSize: `large`,
            color: `#7A903E`,
            marginTop: theme.spacing(2),
        },
        cautions_select: {
            color: `#7A903E`,
            marginTop: theme.spacing(1),
        },
        cautions: {
            fontSize: `small`,
            marginTop: theme.spacing(1),
        },
        infoButton: {
            marginTop: theme.spacing(1),
        },
    })
);

registerLocale(ConfigGetter.get(ConfigKey.CALENDAR_LOCALE), ja);

/**
 * カレンダー
 */
export const Calendar: React.FC = () => {
    const classes = useStyles();
    const isPcSize = useMediaQuery(CommonConstants.RESPONSIVE_MIN_WIDTH);

    /**
     * 予定を追加する際にCalendarオブジェクトのメソッドを使用する必要がある。
     * (CalendarオブジェクトはRef経由でアクセスする必要がある。)
     */
    let ref = React.createRef<any>();

    const [events, setEvents] = useState<EventType[]>([]); // すべてのイベント情報
    // 表示モード（`dayGridMonth`：月、`timeGridWeek`：週、`timeGridDay`：日、`listWeek`：週）
    const [viewMode, setViewMode] = useState<string>(() => {
        const mode = sessionStorage.getItem(CommonConstants.SESSION_KEY_VIEW_MODE);
        if (mode !== null) {
            return mode;
        }
        return CommonConstants.VIEWMODE_DAY_GRID_MONTH;
    });
    // 対象の予約
    const [targetBooking, setTargetBooking] = useState<EventType>({
        id: ``,
        title: ``,
        start: new Date(),
        end: new Date(),
        color: ``,
        display: ``,
        classNames: [],
        groupId: ``,
        bookingTypeId: 0,
        status: 0,
        subscriberName: ``,
        subscriberLaboratoryId: ``,
        subscriberLaboratoryName: ``,
        subscriberMailAddress: ``,
        subscriberMailAddressOption: ``,
        subscriberTel: ``,
        subscriberMemo: ``,
        bookingDetails: [],
    });
    // 空予約に採番している一時的なID（登録後にイベントから削除するために使用）
    const [freeEventId, setFreeEventId] = useState<string>(``);

    // モーダル
    const [isOpenForConfirmModal, setOpenForConfirmModal] = useState<boolean>(false);
    const [messageForConfirmModal, setMessageForConfirmModal] = useState<string>(``);
    const [isOpenForSolventsInfoByBookingTypeDialog, setOpenForSolventsInfoByBookingTypeDialog] = useState<boolean>(
        false
    );

    // ダイアログ
    const [isOpenForSubscribeDialog, setOpenForSubscribeDialog] = useState<boolean>(false);
    const [isOpenForSelectBookingChangeorDeleteDialog, setOpenForSelectBookingChangeorDeleteDialog] = useState<boolean>(
        false
    );
    const [isOpenForConfirmDialog, setOpenForConfirmDialog] = useState<boolean>(false);
    // ローディング
    const [isLoading, setLoading] = useState<boolean>(false);
    // ステッパー
    const [activeStep, setActiveStep] = useState(CommonConstants.SELECT_BOOKING_TYPE_DIALOG);

    // 予約対象溶媒
    const [targetBookingSolvent, setTargetBookingSolvent] = useState<Solvent[]>([]);

    // 全ての溶媒情報
    const [allSolvents, setAllSolvents] = useState<Solvent[]>([]);

    // 表示期間
    const [startViewDate, setStartViewDate] = useState<Date>(() => {
        const sessionDate = sessionStorage.getItem(CommonConstants.SESSION_KEY_START_VIEW_DATE);
        if (sessionDate !== null) {
            return new Date(sessionDate);
        }
        let date: Date = new Date();
        date.setDate(1);
        return date;
    });
    const [endViewDate, setEndViewDate] = useState<Date>(() => {
        const sessionDate = sessionStorage.getItem(CommonConstants.SESSION_KEY_END_VIEW_DATE);
        if (sessionDate !== null) {
            return new Date(sessionDate);
        }
        let date: Date = new Date();
        date.setMonth(date.getMonth() + 1);
        date.setDate(0);
        return date;
    });
    // カレンダーに表示する最初の日にち
    const [initialDate] = useState<Date>(() => {
        let date: Date;
        const sessionDate = sessionStorage.getItem(CommonConstants.SESSION_KEY_START_VIEW_DATE);
        if (sessionDate === null) {
            date = new Date();
            date.setDate(1);
        } else {
            date = new Date(sessionDate);
        }

        if (viewMode === CommonConstants.VIEWMODE_DAY_GRID_MONTH && date.getDate() !== 1) {
            date.setDate(1);
            date.setMonth(date.getMonth() + 1);
        }
        return date;
    });

    // 予約可能溶媒
    const [bookableSolvent, setBookableSolvent] = useState<Solvent[]>([]);

    // エラーメッセージ
    const [errorMessage, setErrorMessage] = useState<string>(``);
    const { success } = useContext(SigninContext);

    useEffect(() => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 予約情報を初期化
            if (ref.current !== null) {
                ref.current.getApi().removeAllEvents();
            }

            // 予約情報を取得
            let result = await BookingApi.getBooking(startViewDate, endViewDate);
            if (result?.status === 200) {
                const bookingEntities: Booking[] = Array.from(result?.data.bookings);
                const bookings = convertBookings(bookingEntities);
                // カレンダーに表示するイベント（予約、休日）を設定
                setAllEvents(bookings);
            }

            // 全ての溶媒情報を取得
            let solvents = await SolventApi.getSolvent();
            if (solvents?.status === 200) {
                const allSolvent: Solvent[] = Array.from(solvents?.data.solvents);
                setAllSolvents(allSolvent);
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
        setErrorMessage(``);
    }, [viewMode, startViewDate, endViewDate, success]);

    /**
     * DBから取得した予約情報を、イベント情報として持ち回せるよう詰め替える(予約情報複数件の場合)
     * @param bookingEntities DBから取得した予約情報
     * @return 予約イベント
     */
    const convertBookings = (bookingEntities: Booking[]): EventType[] => {
        let bookings: EventType[] = [];
        bookingEntities.forEach((entity) => {
            bookings.push(convertBooking(entity));
        });
        return bookings;
    };

    /**
     * カレンダーに表示するイベントを設定する
     * @param bookings 予約イベント
     */
    const setAllEvents = async (bookings: EventType[]) => {
        // カレンダーに表示する予約、休日、空予約情報を設定
        let events: EventType[] = await getBookingEvents(bookings);
        events.push(...getHolidayEvents());
        setEvents(events);
    };

    /**
     * 予約可能日時を設定する
     * @param bookings 予約イベント
     * @param date 押下された日付
     */
    const setBookings = (bookings: EventType[], date: Date) => {
        const events: EventType[] = [];
        events.push(...getFreeBookingEvents(bookings));
        setEvents(events);
    };

    /**
     * カレンダーに表示する予約イベントを取得
     * @param bookings 予約イベント
     * @return 予約イベント
     */
    const getBookingEvents = async (bookingsOrg: EventType[]): Promise<EventType[]> => {
        if (getViewMode() !== CommonConstants.VIEWMODE_LIST_WEEK) {
            // リスト表示以外の場合は、イベントに予約情報を持たせない
            return [];
        }

        // ログインユーザ情報取得
        const user: User = await UserApi.getUser();
        if (!user.signin) {
            // 未ログインの場合は、イベントに予約情報を持たせない
            return [];
        }

        // ログイン済みの場合、自身が所属する研究室の予約をイベントに追加
        return bookingsOrg.filter((row) => row.subscriberLaboratoryId === user.laboid);
    };

    /**
     * 表示モードを取得
     */
    const getViewMode = () => {
        let mode;
        if (ref.current !== null) {
            // refがある場合は、refから取得
            mode = ref.current.getApi().view.type;
        } else {
            // refがない場合は、セッションから取得
            mode = sessionStorage.getItem(CommonConstants.SESSION_KEY_VIEW_MODE);
            if (mode != null) {
                return mode;
            }
            // デフォルト値
            mode = CommonConstants.VIEWMODE_DAY_GRID_MONTH;
        }

        return mode;
    };

    /**
     * 休日情報を取得
     * （初回のみプロパティから取得し、次回以降はsessionストレージから取得する）
     * @return 休日情報
     */
    const getHolidays = (): string[] => {
        let holidays: string[] = [];
        // 休日情報を即時反映するため、常にプロパティから取得(ストレージにも念のため格納しておく)
        // if (sessionStorage.getItem(CommonConstants.SESSION_KEY_HOLIDAYS) === null) {
            sessionStorage.setItem(
                CommonConstants.SESSION_KEY_HOLIDAYS,
                JSON.stringify(ConfigGetter.get(ConfigKey.CAMPUS_HOLIDAY))
            );
        // } else {
            const holidaysJson = sessionStorage.getItem(CommonConstants.SESSION_KEY_HOLIDAYS);
            if (typeof holidaysJson === `string`) {
                holidays = JSON.parse(holidaysJson);
            }
        // }
        return holidays;
    };

    /**
     * 休日情報を取得
     * @return 休日イベント
     */
    const getHolidayEvents = (): EventType[] => {
        const holidays: string[] = getHolidays();
        let events: EventType[] = [];

        // 表示期間内の休日情報を設定
        if (holidays.length > 0) {
            holidays.forEach((date: string) => {
                let start = new Date(date);
                let end = new Date(date);
                end.setDate(end.getDate() + 1);
                if (start.getTime() >= startViewDate.getTime() && start.getTime() <= endViewDate.getTime()) {
                    events.push({
                        id: ``,
                        title: ``,
                        start: start,
                        end: end,
                        color: CommonConstants.EVENT_HOLIDAY_COLOR,
                        display: CommonConstants.EVENT_DISPLAY_HOLIDAY,
                        classNames: [classes.holiday],
                        groupId: CommonConstants.GROUP_ID_HOLIDAY,
                        bookingTypeId: DbValueConstants.BOOKING_TYPE_ID_TAKEOUT,
                    });
                }
            });
        }

        return events;
    };

    /**
     * 空予約情報を取得
     * @param bookings 予約イベント
     * @return 空予約イベント
     */
    const getFreeBookingEvents = (bookings: EventType[]): EventType[] => {
        let events: EventType[] = [];
        // 予約が入っていない時間帯に対して、動的に空予約を作る
        events.push(...createFreeBookingEventsByBookingType(bookings, DbValueConstants.BOOKING_TYPE_ID_TAKEOUT));
        events.push(...createFreeBookingEventsByBookingType(bookings, DbValueConstants.BOOKING_TYPE_ID_POMPOUT1));
        events.push(...createFreeBookingEventsByBookingType(bookings, DbValueConstants.BOOKING_TYPE_ID_POMPOUT2));

        return events;
    };

    /**
     * 予約種別IDごとに空予約を作成する
     * @param bookings 予約イベント
     * @param bookingTypeId 予約種別ID
     * @return 空予約イベント
     */
    const createFreeBookingEventsByBookingType = (bookings: any[], bookingTypeId: number): EventType[] => {
        // 予約時間
        const startTimeAm = ConfigGetter.getNum(ConfigKey.BOOKING_START_TIME_AM);
        const endTimeAm = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME_AM);
        const startTimePm = ConfigGetter.getNum(ConfigKey.BOOKING_START_TIME_PM);
        const endTimePm = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME_PM);
        const bookingableDays = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_DAYS);
        const interval = getInterval(bookingTypeId);

        const bookableMinDate = getBookableMinDate(startTimeAm);
        const bookableMaxDate = new Date(bookableMinDate);
        bookableMaxDate.setDate(bookableMaxDate.getDate() + bookingableDays);
        bookableMaxDate.setHours(endTimePm);

        let start = new Date(bookableMinDate);
        let end;

        let events: EventType[] = [];
        let id = 0;
        while (start.getTime() < bookableMaxDate.getTime()) {
            if (isHoliday(start)) {
                // 休日の場合は予約不可
                start.setDate(start.getDate() + 1);
                start.setHours(startTimeAm);
                continue;
            }

            if (isBooked(start, bookingTypeId, bookings)) {
                // 予約済みの場合は予約不可
                start.setMinutes(start.getMinutes() + interval);
                continue;
            }

            // お昼時間は予約不可
            if (start.getHours() >= endTimeAm && start.getHours() < startTimePm) {
                start.setHours(startTimePm);
                continue;
            }

            if (start.getHours() >= endTimePm) {
                // 指定時間以降のため予約不可
                start.setDate(start.getDate() + 1);
                start.setHours(startTimeAm);
                continue;
            }
            end = new Date(start);
            end.setMinutes(end.getMinutes() + interval);
            events.push({
                id: id.toString(),
                title: ``,
                start: start,
                end: end,
                color: BookingUtil.getColorByBookingType(bookingTypeId),
                display: CommonConstants.EVENT_DISPLAY_BOOKING,
                classNames: [`${BookingUtil.getClassNameByBookingType(bookingTypeId)}`],
                groupId: CommonConstants.GROUP_ID_FREE,
                extendedProps: {
                    // eventから取得できるように予約種別を保持
                    bookingTypeId: bookingTypeId,
                },
                bookingTypeId: bookingTypeId,
                status: 0,
            });
            start = new Date(end);
            id++;
        }
        return events;
    };

    /**
     * 午前・午後の見出しイベントを作成する
     * @returns 午前・午後の見出しイベント
     */
    const createEventPartition = () => {
        // 予約時間
        const startTimeAm = ConfigGetter.getNum(ConfigKey.BOOKING_START_TIME_AM);
        const endTimeAm = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME_AM);
        const startTimePm = ConfigGetter.getNum(ConfigKey.BOOKING_START_TIME_PM);
        const endTimePm = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME_PM);
        const bookingableDays = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_DAYS);
        const bookingableLimitAm = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_LIMIT_AM);

        const bookableMinDate = getBookableMinDate(bookingableLimitAm);
        const bookableMaxDate = new Date(bookableMinDate);
        bookableMaxDate.setDate(bookableMaxDate.getDate() + bookingableDays);
        bookableMaxDate.setHours(endTimePm);

        let start = new Date(bookableMinDate);

        let events: EventType[] = [];

        // 午前・午後の見出しイベントを設定
        const setEvent = (id: string, title: string, start: Date, end: Date) => {
            events.push({
                id,
                title,
                start,
                end,
                color: CommonConstants.EVENT_PARTITION_COLOR,
                display: CommonConstants.EVENT_DISPLAY_BOOKING,
                classNames: [CommonConstants.BOOKING_TYPE_CLASS_EVENT_PARTITION],
                groupId: CommonConstants.GROUP_ID_FREE,
                extendedProps: {
                    bookingTypeId: undefined,
                },
                bookingTypeId: 0,

                status: 0,
            });
        };

        while (start.getTime() < bookableMaxDate.getTime()) {
            if (isHoliday(start)) {
                // 休日の場合は予約不可
                start.setDate(start.getDate() + 1);
                start.setHours(bookingableLimitAm);
                continue;
            }
            // 午前の見出しを作成
            if (start.getHours() < startTimeAm) {
                setEvent(DateUtil.parseDate(start) + `_partition_am`, `午前`, new Date(start), new Date(startTimeAm));
            }
            start.setHours(endTimeAm);
            // 午後の見出しを作成
            if (start.getHours() >= endTimeAm && start.getHours() < startTimePm) {
                setEvent(DateUtil.parseDate(start) + `_partition_pm`, `午後`, new Date(start), new Date(startTimePm));
            }
            start.setDate(start.getDate() + 1);
            start.setHours(bookingableLimitAm);
        }
        return events;
    };

    /**
     * 予約可能最小日を取得する
     * @param startTimeAm 午前の予約開始時間
     * @returns 予約可能最小日
     */
    const getBookableMinDate = (startTimeAm: number) => {
        // 予約時間
        const now = new Date();
        const endTimeAm = ConfigGetter.getNum(ConfigKey.BOOKING_END_TIME_AM);
        const bookingableLimitAm = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_LIMIT_AM);
        const bookingableLimitPm = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_LIMIT_PM);

        // 予約開始時間を判定
        let bookableMinDate = new Date(now);
        bookableMinDate.setMinutes(0);
        bookableMinDate.setSeconds(0);
        bookableMinDate.setMilliseconds(0);
        if (now.getHours() < bookingableLimitAm) {
            // 午前から
            bookableMinDate.setHours(startTimeAm);
        } else if (now.getHours() > bookingableLimitPm) {
            // 翌日から
            bookableMinDate.setDate(bookableMinDate.getDate() + 1);
            bookableMinDate.setHours(startTimeAm);
        } else {
            // 午後から
            bookableMinDate.setHours(endTimeAm);
        }

        return bookableMinDate;
    };

    /**
     * 予約済みかどうか判定
     * @param start 予約開始日時
     * @param bookings 予約種別ID
     * @param bookings 予約情報
     * @return 予約済みの場合true
     */
    const isBooked = (start: Date, bookingTypeId: number, bookings: EventType[]): boolean => {
        for (let i = 0; i < bookings.length; i++) {
            if (bookings[i].start.getTime() === start.getTime() && bookings[i].bookingTypeId === bookingTypeId) {
                return true;
            }
        }
        return false;
    };

    /**
     * 予約種別IDに紐づく予約間隔時間を取得
     * @param bookingTypeId 予約種別ID
     * @return 予約間隔時間
     */
    const getInterval = (bookingTypeId: number): number => {
        let interval = 0;
        switch (bookingTypeId) {
            case 1:
                interval = ConfigGetter.getNum(ConfigKey.BOOKING_INTERVAL_TAKEOUT);
                break;
            case 2:
                interval = ConfigGetter.getNum(ConfigKey.BOOKING_INTERVAL_POMPOUT1);
                break;
            case 3:
                interval = ConfigGetter.getNum(ConfigKey.BOOKING_INTERVAL_POMPOUT2);
                break;
            default:
                break;
        }
        return interval;
    };

    /**
     * 日時から予約情報を作成（新規登録用）
     * @param date
     * @return 予約情報
     */
    const createInitializeBooking = (date: any): EventType => {
        return {
            id: ``,
            title: ``,
            start: date,
            end: new Date(),
            color:  ``,
            display:  ``,
            groupId: CommonConstants.GROUP_ID_FREE,
            bookingTypeId: DbValueConstants.BOOKING_TYPE_ID_TAKEOUT,

        };
    };

    /**
     * イベントがクリックされた場合に行う処理
     * @param arg 選択されたイベント情報
     */
    const handleCLick = (arg: any) => {
        // 午前・午後の見出しイベントの場合
        if (arg.event.extendedProps.bookingTypeId === undefined) return;

        setActiveStep(CommonConstants.SELECT_BOOKING_TYPE_DIALOG);
        const curDate = new Date();
        setErrorMessage(``);

        // 予約詳細情報を取得
        const executeAboutBookingdetail = async () => {
            // ローディング開始
            setLoading(true);

            // 予約可能な溶媒を取得
            let detail = await BookableSolventApi.getBookableSolvent(
                DateUtil.parseDate(arg.event.start),
                DateUtil.parseTime(arg.event.start),
                DateUtil.parseTime(arg.event.end),
                arg.event.extendedProps.bookingTypeId
            );
            if (detail?.status === 200) {
                const bookableSolvents: Solvent[] = Array.from(detail?.data.solvents);
                setBookableSolvent(bookableSolvents);
                // 当日以降の予約の場合
                if (curDate.setHours(curDate.getHours()) < arg.event.start) {
                    // 休日以外の場合
                    if (arg.event.groupId !== CommonConstants.GROUP_ID_HOLIDAY) {
                        if (arg.event.groupId === CommonConstants.GROUP_ID_FREE) {
                            // 新規登録
                            // 予約枠が空いているかどうかチェック
                            let result: any = await BookingApi.checkFree(
                                arg.event.start,
                                arg.event.end,
                                arg.event.extendedProps.bookingTypeId
                            );
                            if (!result.data?.success) {
                                if (result.data?.errorMessage !== null) {
                                    setErrorMessage(result.data?.errorMessage);
                                } else {
                                    setErrorMessage(result.errors?.errorMessage);
                                }
                                return;
                            }
                            setFreeEventId(arg.event.id);
                            setTargetBooking(createInitializeBooking(arg.event));
                            setOpenForSubscribeDialog(true);
                            setBookableSolvent(bookableSolvents);
                            setTargetBookingSolvent([]);
                        } else {
                            // 変更・取消選択
                            const event = await BookingApi.getBookingForConfirm(arg.event.id);
                            if (event?.status === 200 && event.data.id !== null) {
                                const bookingEntities: Booking = event.data;
                                const booking = convertBooking(bookingEntities);
                                setTargetBooking(booking);
                                setOpenForSelectBookingChangeorDeleteDialog(true);
                                setBookableSolvent(bookableSolvents);
                                setTargetBookingSolvent(getTargetBookingSolbent(booking.bookingDetails));
                            }
                        }
                    }
                } else {
                    // 当日より前の日付の予約の場合（予約確認のみ）
                    const event = await BookingApi.getBookingForConfirm(arg.event.id);
                    if (event?.status === 200) {
                        const bookingEntities: Booking = event.data;
                        const booking = convertBooking(bookingEntities);
                        setTargetBooking(booking);
                        setOpenForConfirmDialog(true);
                        setTargetBookingSolvent(getTargetBookingSolbent(booking.bookingDetails));
                    }
                }
            }
            // ローディング終了
            setLoading(false);
        };
        executeAboutBookingdetail();
    };

    /**
     * DBから取得した予約情報を、イベント情報として持ち回せるよう詰め替える(予約情報一件の場合)
     * @param bookingEntities DBから取得した予約情報
     * @return 予約イベント
     */
    const convertBooking = (bookingEntities: Booking): EventType => {
        let booking: EventType = {
            id: bookingEntities.id.toString(),
            title: ``,
            start: new Date(bookingEntities.bookingStartTime),
            end: new Date(bookingEntities.bookingEndTime),
            color: `${BookingUtil.getColorByBookingType(bookingEntities.bookingTypeId)}`,
            display: CommonConstants.EVENT_DISPLAY_BOOKING,
            classNames: [`${BookingUtil.getClassNameByBookingType(bookingEntities.bookingTypeId)}`],
            groupId: `booking`,
            bookingTypeId: bookingEntities.bookingTypeId,
            status: bookingEntities.status,
            subscriberName: bookingEntities.subscriberName,
            subscriberLaboratoryId: bookingEntities.subscriberLaboratoryId,
            subscriberLaboratoryName: bookingEntities.subscriberLaboratoryName,
            subscriberMailAddress: bookingEntities.subscriberMailAddress,
            subscriberMailAddressOption: bookingEntities.subscriberMailAddressOption,
            subscriberTel: bookingEntities.subscriberTel,
            subscriberMemo: bookingEntities.subscriberMemo,
            bookingDetails: bookingEntities.bookingDetails,
        };
        return booking;
    };

    /**
     * 予約詳細情報から、予約されている溶媒を抽出する
     * @param bookingDetails 予約詳細
     * @return 予約されている溶媒
     */
    const getTargetBookingSolbent = (bookingDetails: BookingDetail[] | undefined): Solvent[] => {
        let solvents: Solvent[] = [];
        if (bookingDetails === undefined) {
            return solvents;
        }

        bookingDetails.forEach((row) => {
            solvents.push({
                id: row.solventId,
                capacity: row.capacity,
                capacityKg: row.capacityKg,
                unit: row.unit,
                name: row.name,
                subName: row.subName,
                dailyLimit: row.dailyLimit,
                kgPerKan: row.kgPerKan,
                literPerKan: row.literPerKan,
                specificGravity: row.specificGravity,
                pump1Flag: row.pump1Flag,
                pump2Flag: row.pump2Flag,
                colorCode: row.colorCode,
            });
        });
        return solvents;
    };

    /**
     * 指定のIDに紐づくイベントを取得
     * @param id イベントID
     * @return イベント
     */
    const getEventById = (id: string): EventType => {
        for (let i = 0; i < events.length; i++) {
            if (id === events[i].id.toString() && id !== ``) {
                return events[i];
            }
        }
        // イベントIDがnullの場合、空のイベントを返却する
        const event: EventType = {
            id: ``,
            title: ``,
            start: new Date(),
            end: new Date(),
            color: ``,
            display: ``,
            classNames: [],
            groupId: ``,
            bookingTypeId: 0,
            status: 0,
            subscriberName: ``,
            subscriberLaboratoryId: ``,
            subscriberLaboratoryName: ``,
            subscriberMailAddress: ``,
            subscriberMailAddressOption: ``,
            subscriberTel: ``,
            subscriberMemo: ``,
            bookingDetails: [],
        };
        return event;
    };

    /**
     * 予約種別名を取得
     * @param id イベントID
     * @return 予約種別名
     */
    const getBookingTypeName = (id: number | undefined): string => {
        let value = ``;
        switch (id) {
            case DbValueConstants.BOOKING_TYPE_ID_TAKEOUT:
                value = CommonConstants.BOOKING_TYPE_NAME_TAKEOUT_FOR_LIST;
                break;
            case DbValueConstants.BOOKING_TYPE_ID_POMPOUT1:
                value = CommonConstants.BOOKING_TYPE_NAME_POMPOUT1_FOR_LIST;
                break;
            case DbValueConstants.BOOKING_TYPE_ID_POMPOUT2:
                value = CommonConstants.BOOKING_TYPE_NAME_POMPOUT2_FOR_LIST;
                break;
            default:
                break;
        }
        return value;
    };

    /**
     * イベントごとのレンダリング時に行う処理
     *
     * @param info 対象イベント
     */
    const renderEventContent = (info: EventContentArg) => {
        if (getViewMode() !== CommonConstants.VIEWMODE_LIST_WEEK) {
            // リスト表示以外の場合は処理なし
            return;
        }

        if (info.event.id !== "") {
            const event: EventType = getEventById(info.event.id);
            if (event === null || event.bookingDetails === null) {
                return;
            }

            // リスト表示の場合、予約詳細情報を一覧に追加する
            // 画面サイズ（大）
            if (isPcSize) {
                const detailCount = event.bookingDetails?.length;
                return (
                    <table>
                        <tr>
                            <th className={classes.listTable_th}>名前</th>
                            <th className={classes.listTable_th}>研究室</th>
                            <th className={classes.listTable_th}>予約種別</th>
                            <th className={classes.listTable_th}>溶媒/容量</th>
                        </tr>
                        {event.bookingDetails?.map((row, i) => (
                            <tr>
                                {i === 0 && (
                                    <>
                                        <td
                                            rowSpan={detailCount}
                                            className={classes.listTable_td + " " + classes.listTable_tdSubscriberName}
                                        >
                                            {event.subscriberName}
                                        </td>
                                        <td
                                            rowSpan={detailCount}
                                            className={classes.listTable_td + " " + classes.listTable_tdLaboratory}
                                        >
                                            {event.subscriberLaboratoryName}
                                        </td>
                                        <td
                                            rowSpan={detailCount}
                                            className={classes.listTable_td + " " + classes.listTable_tdBookingType}
                                        >
                                            {getBookingTypeName(event.bookingTypeId)}
                                        </td>
                                    </>
                                )}
                                <td className={classes.listTable_td + " " + classes.listTable_tdSolventName}>
                                    {row.name}（{row.subName}）/ {row.capacity.toString()}
                                    {row.unit === DbValueConstants.UNIT_ID_KG
                                        ? CommonConstants.UNIT_NAME_KG
                                        : CommonConstants.UNIT_NAME_L}
                                </td>
                            </tr>
                        ))}
                    </table>
                );
            }

            // 画面サイズ（小）
            return (
                <table>
                    <tr>
                        <th className={classes.listTable_th}>名前</th>
                    </tr>
                    <tr className={classes.listTable_th}>
                        <td className={classes.listTable_td + " " + classes.listTable_tdSubscriberName}>
                            {event.subscriberName}
                        </td>
                    </tr>
                    <tr>
                        <th className={classes.listTable_th}>研究室</th>
                    </tr>
                    <tr>
                        <td className={classes.listTable_td + " " + classes.listTable_tdLaboratory}>
                            {event.subscriberLaboratoryName}
                        </td>
                    </tr>
                    <tr>
                        <th className={classes.listTable_th}>予約種別</th>
                    </tr>
                    <tr>
                        <td className={classes.listTable_td + " " + classes.listTable_tdBookingType}>
                            {getBookingTypeName(event.bookingTypeId)}
                        </td>
                    </tr>
                    <tr>
                        <th className={classes.listTable_th}>溶媒/容量</th>
                    </tr>
                    {event.bookingDetails?.map((row) => (
                        <tr>
                            <td className={classes.listTable_td + " " + classes.listTable_tdSolventName}>
                                {row.name}（{row.subName}）/ {row.capacity.toString()}
                                {row.unit === DbValueConstants.UNIT_ID_KG
                                    ? CommonConstants.UNIT_NAME_KG
                                    : CommonConstants.UNIT_NAME_L}
                            </td>
                        </tr>
                    ))}
                </table>
            );
        }
    };

    /**
     * 表示する日付（期間）が変更された場合に行う処理
     *
     * @param start 表示開始日
     * @param end 表示終了日
     */
    const handleChangeDate = (start: Date, end: Date) => {
        // カレンダーに表示する最初の日にちを判定
        let initialDate: Date = start;
        if (getViewMode() === CommonConstants.VIEWMODE_DAY_GRID_MONTH && start.getDate() !== 1) {
            // 月初が1日でない場合があるので、強制的に表示月の1日を指定
            initialDate.setDate(1);
            initialDate.setMonth(initialDate.getMonth() + 1);
        }
        // カレンダーの表示開始、終了日を設定
        if (start.toString() !== startViewDate.toString()) {
            sessionStorage.setItem(CommonConstants.SESSION_KEY_START_VIEW_DATE, start.toString());
            setStartViewDate(start);
        }
        if (end.toString() !== endViewDate.toString()) {
            sessionStorage.setItem(CommonConstants.SESSION_KEY_END_VIEW_DATE, end.toString());
            setEndViewDate(end);
        }
    };

    /**
     * 次のステップへ移動する処理
     */
    const nextStep = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    /**
     * 前のステップへ移動する処理
     */
    const backStep = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    /**
     * 初期表示時、および表示形式（月、週、日、リスト）が切り替わったタイミングで行う処理
     */
    const viewDidMount = () => {
        if (ref.current === null) {
            setViewMode(CommonConstants.VIEWMODE_DAY_GRID_MONTH);
            return;
        }
        // 予約情報を初期化
        ref.current.getApi().removeAllEvents();
        const viewMode = ref.current.getApi().view.type;
        sessionStorage.setItem(CommonConstants.SESSION_KEY_VIEW_MODE, viewMode);
        setViewMode(viewMode);
    };

    /**
     * 休日かどうか判定
     * @param targetDate 判定対象日時
     * @return 休日の場合true
     */
    const isHoliday = (targetDate: Date): boolean => {
        const date = new Date(targetDate);
        // 土日かどうか
        if (date.getDay() === 0 || date.getDay() === 6) {
            return true;
        }
        // 休日かどうか
        date.setHours(0);
        const holidays: string[] = getHolidays();
        for (let i = 0; i < holidays.length; i++) {
            if (date.getTime() === new Date(holidays[i]).getTime()) {
                return true;
            }
        }
        // 営業日の場合
        return false;
    };

    /**
     * 日毎に行う処理
     * @param arg イベント
     */
    const handleDayCellClassNames = (arg: any) => {
        // 月表示の場合、休日の日に色を付ける
        if (arg.view.type === CommonConstants.VIEWMODE_DAY_GRID_MONTH) {
            if (isHoliday(arg.date)) {
                return [classes.holiday];
            }
            // 予約可能日に色をつける
            if (isBookableDate(arg.date)) {
                return [classes.bookable_days];
            }
        }
        return [];
    };

    const setBookingData = () => {
        // 選択された溶媒を予約情報に追加
        let bookingDetails: BookingDetail[] = [];
        targetBookingSolvent.forEach((row) => {
            bookingDetails.push({
                solventId: row.id,
                capacity: row.capacity,
                capacityKg: row.capacityKg,
                unit: row.unit,
                name: row.name,
                subName: row.subName,
                dailyLimit: row.dailyLimit,
                kgPerKan: row.kgPerKan,
                literPerKan: row.literPerKan,
                specificGravity: row.specificGravity,
                pump1Flag: row.pump1Flag,
                pump2Flag: row.pump2Flag,
                colorCode: row.colorCode,
            });
        });
        targetBooking.bookingDetails = bookingDetails;
        setTargetBooking(targetBooking);
    };

    /**
     * 予約の「登録」ボタン押下時の処理
     */
    const createBooking = () => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 登録
            let result: any = await BookingApi.createBooking(targetBooking);
            if (result.data?.success) {

                // 更新内容を反映
                setEvents(getRemoveEventById(freeEventId));
                nextStep();

                // メール送信
                const contents: any = await MailApi.getMailMessage();
                if (contents?.status === 200) {
                    const mail: Mail = {
                        id: Number(result.data.id),
                        to: selectSubscriberMailAddress(),
                        subject: contents.data.createSubject,
                        message: contents.data.createMessage,
                    };
                    result = await MailApi.sendMail(mail);
                }
            } else {
                // エラーメッセージ表示
                if (result.data !== undefined) {
                    if (result.data?.errorMessage !== null) {
                        setErrorMessage(result.data?.errorMessage);
                    }
                } else {
                    if (result.errors?.errorMessage !== null) {
                        setErrorMessage(result.errors?.errorMessage);
                    }
                }
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
    };

    /**
     * 予約の「変更」ボタン押下時の処理
     */
    const updateBooking = () => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 更新
            let result: any = await BookingApi.updateBooking(targetBooking);
            if (result.data?.success) {

                // 更新内容を反映
                let newEvents = getRemoveEventById(targetBooking.id);
                newEvents.push(targetBooking);
                setEvents(newEvents);
                nextStep();

                // メール送信
                const contents: any = await MailApi.getMailMessage();
                if (contents?.status === 200) {
                    const mail: Mail = {
                        id: Number(targetBooking.id),
                        to: selectSubscriberMailAddress(),
                        subject: contents.data.updateSubject,
                        message: contents.data.updateMessage,
                    };
                    result = await MailApi.sendMail(mail);
                }
            } else {
                // エラーメッセージ表示
                if (result.data !== undefined) {
                    if (result.data?.errorMessage !== null) {
                        setErrorMessage(result.data?.errorMessage);
                    }
                } else {
                    if (result.errors?.errorMessage !== null) {
                        setErrorMessage(result.errors?.errorMessage);
                    }
                }
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
    };

    /**
     * 予約の「取消」ボタン押下時の処理
     */
    const deleteBooking = () => {
        const execute = async () => {
            // ローディング開始
            setLoading(true);

            // 削除
            let result: any = await BookingApi.deleteBooking(parseInt(targetBooking.id));
            if (result.data?.success) {
                // 更新内容を反映
                setEvents(getRemoveEventById(targetBooking.id));
                setOpenForConfirmModal(false);
                setOpenForSelectBookingChangeorDeleteDialog(false);
                setBookableSolvent([]);
                setTargetBookingSolvent([]);

                // メール送信
                const contents: any = await MailApi.getMailMessage();
                if (contents?.status === 200) {
                    const mail: Mail = {
                        id: Number(targetBooking.id),
                        to: selectSubscriberMailAddress(),
                        subject: contents.data.deleteSubject,
                        message: contents.data.deleteMessage,
                    };
                    result = await MailApi.sendMail(mail);
                }
            } else {
                // エラーメッセージ表示
                if (result.data !== undefined) {
                    if (result.data?.errorMessage !== null) {
                        setErrorMessage(result.data?.errorMessage);
                    }
                } else {
                    if (result.errors?.errorMessage !== null) {
                        setErrorMessage(result.errors?.errorMessage);
                    }
                }
            }

            // ローディング終了
            setLoading(false);
        };
        execute();
    };

    /**
     * 対象のイベントIDに紐づくイベントを削除
     * @param eventId イベントID
     * @return 対象のイベントを削除したイベント情報
     */
    const getRemoveEventById = (eventId: string) => {
        return events.filter((row) => {
            if (row.id !== eventId) {
                return row;
            }
        });
    };

    /**
     * メールの送信先を選択する処理
     */
    const selectSubscriberMailAddress = () => {
        let mailAddresses = [];
        mailAddresses.push(
            targetBooking.subscriberMailAddress !== undefined ? targetBooking.subscriberMailAddress : ``
        );

        if (
            targetBooking.subscriberMailAddressOption !== undefined &&
            targetBooking.subscriberMailAddressOption !== null &&
            targetBooking.subscriberMailAddressOption !== ``
        ) {
            mailAddresses.push(targetBooking.subscriberMailAddressOption);
        }
        return mailAddresses;
    };

    /**
     * カレンダー設定用にイベントを詰め替える
     * @return イベント情報
     */
    const convertEventsForCalendar = () => {
        let convertedEvents: any[] = [];
        events.forEach((event) => {
            convertedEvents.push({
                id: event.id,
                title: event.title,
                start: event.start,
                end: event.end,
                color: event.color,
                display: event.display,
                classNames: event.classNames,
                groupId: event.groupId,
                extendedProps: event.extendedProps,
                bookingTypeId: event.bookingTypeId,
                status: event.status,
                subscriberName: event.subscriberName,
                subscriberLaboratoryId: event.subscriberLaboratoryId,
                subscriberLaboratoryName: event.subscriberLaboratoryName,
                subscriberMailAddress: event.subscriberMailAddress,
                subscriberMailAddressOption: event.subscriberMailAddressOption,
                subscriberTel: event.subscriberTel,
                subscriberMemo: event.subscriberMemo,
                bookingDetails: event.bookingDetails,
            });
        });
        return convertedEvents;
    };

    /**
     * 予約時入力チェック（溶媒情報）
     */
    const checkBookingDetail = async () => {
        let result: any = await BookingApi.checkBookingDetail(targetBooking);
        if (result.data?.success) {
            nextStep();
            setErrorMessage(``);
        } else {
            // エラーメッセージ表示
            if (result.data !== undefined) {
                if (result.data?.errorMessage !== null) {
                    setErrorMessage(result.data?.errorMessage);
                }
            } else {
                if (result.errors?.errorMessage !== null) {
                    setErrorMessage(result.errors?.errorMessage);
                }
            }
        }
    };

    /**
     * 予約時入力チェック（予約者情報）
     */
    const checkSubscriberInfo = async () => {
        let result: any = await BookingApi.checkSubscriberInfo(targetBooking);
        if (result.data?.success) {
            nextStep();
            setErrorMessage(``);
        } else {
            // エラーメッセージ表示
            if (result.data !== undefined) {
                if (result.data?.errorMessage !== null) {
                    setErrorMessage(result.data?.errorMessage);
                }
            } else {
                if (result.errors?.errorMessage !== null) {
                    setErrorMessage(result.errors?.errorMessage);
                }
            }
        }
    };

    /**
     * 午前・午後の見出しイベントは予約開始時間を表示させないようにする
     */
    const eventDidMount = (e: any) => {
        const element = e.el;
        // 見出しイベント以外は対象外
        if (!element.classList.contains(CommonConstants.BOOKING_TYPE_CLASS_EVENT_PARTITION)) return;

        // 「月」表示の場合
        if (e.view.type === CommonConstants.VIEWMODE_DAY_GRID_MONTH) {
            element.getElementsByClassName(`fc-event-time`)[0].remove(`div`);
        }
    };

    /**
     * 予約可能日かどうか判定
     * @param targetDate 判定対象日時
     * @return 予約可能日の場合true
     */
    const isBookableDate = (targetDate: Date): boolean => {
        const date = new Date(targetDate);
        const now = new Date();
        const bookingableLimit = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_LIMIT_PM);
        const bookingableDays = ConfigGetter.getNum(ConfigKey.BOOKING_BOOKINGABLE_DAYS);
        // 最小予約可能日を設定
        const min = new Date(date);
        min.setDate(min.getDate());
        min.setHours(bookingableLimit);
        min.setMinutes(0);
        min.setSeconds(0);
        min.setMilliseconds(0);
        // 最大予約可能日を設定
        const max = new Date(now);
        max.setDate(max.getDate() + bookingableDays);
        max.setHours(0);
        max.setMinutes(0);
        max.setSeconds(0);
        max.setMilliseconds(0);

        return (
            !isHoliday(date) &&
            date.getTime() < max.getTime() &&
            now.getTime() < min.getTime()
        );
    };

    /**
     * カレンダーの日付（or空白）がクリックされた場合に行う処理
     * @param arg 選択された日付情報
     */
     const handleDateClick = (arg: any) => {
        setActiveStep(CommonConstants.SELECT_BOOKING_TYPE_DIALOG);
        setErrorMessage(``);

        // 予約詳細情報を取得
        const executeAboutBookingdetail = async () => {
            // ローディング開始
            setLoading(true);

            let date;
            // 空白を押下した場合
            if (arg.date !== undefined) {
                date = arg.date;
            } else {
                // 日付を押下した場合
                date = arg;
            }

            // 休日の場合、過去日の場合は何もしない
            if (isHoliday(date) || !isBookableDate(date)) {
                // ローディング終了
                setLoading(false);
                return;
            }

            // 予約情報を取得
            const result = await BookingApi.getBooking(startViewDate, endViewDate);
            if (result?.status === 200) {
                const bookingEntities: Booking[] = Array.from(result?.data.bookings);
                const bookings = convertBookings(bookingEntities);
                setBookings(bookings, date);
            }
            setTargetBooking(createInitializeBooking(date));
            setTargetBookingSolvent([]);
            setOpenForSubscribeDialog(true);

            // ローディング終了
            setLoading(false);
        };
        executeAboutBookingdetail();
    };

    return (
        <div className={classes.root}>
            <SubscribeDialog
                isOpen={isOpenForSubscribeDialog}
                onClickCancel={() => {
                    setOpenForSubscribeDialog(false);
                    setErrorMessage(``);
                    setBookableSolvent([]);
                    setTargetBookingSolvent([]);
                }}
                onClickAdd={() => {
                    createBooking();
                }}
                onClickChange={() => {
                    updateBooking();
                }}
                booking={targetBooking}
                handleNext={() => {
                    setBookingData();
                    if (activeStep === CommonConstants.SELECT_BOOKING_TYPE_DIALOG) {
                        nextStep();
                    } else if (activeStep === CommonConstants.SELECT_BOOKING_TIME_DIALOG) {
                        nextStep();
                    } else if (activeStep === CommonConstants.SELECT_SOLVENT_DIALOG) {
                        checkBookingDetail();
                    } else if (activeStep === CommonConstants.SUBSCRIBER_INFO_DIALOG) {
                        checkSubscriberInfo();
                    } else {
                        // なし
                    }
                }}
                handleBack={() => {
                    setErrorMessage(``);
                    if (activeStep === CommonConstants.SELECT_SOLVENT_DIALOG) {
                        setBookableSolvent([]);
                        setTargetBookingSolvent([]);
                    }
                    backStep();
                }}
                step={activeStep}
                bookableSolvent={bookableSolvent}
                setBookableSolvent={(data: Solvent[]) => {
                    setBookableSolvent(data);
                }}
                buttonDelete={(id: number) => {
                    setBookableSolvent((buttons) => buttons.filter((button) => button.id !== id));
                }}
                targetBookingSolvent={targetBookingSolvent}
                setTargetBookingSolvent={(deleteData: Solvent) => {
                    setTargetBookingSolvent((chips) => chips.filter((chip) => chip.id !== deleteData.id));
                }}
                allSolvents={allSolvents}
                errorMessage={errorMessage}
                isLoading={isLoading}
                setLoading={setLoading}
                events={events}
            />
            <SelectBookingChangeOrDeleteDialog
                isOpen={isOpenForSelectBookingChangeorDeleteDialog}
                onClickCancel={() => {
                    setOpenForSelectBookingChangeorDeleteDialog(false);
                    setBookableSolvent([]);
                    setTargetBookingSolvent([]);
                }}
                onClickDelete={() => {
                    setOpenForConfirmModal(true);
                    setMessageForConfirmModal("この予約を取り消しますか？");
                }}
                onClickNextToBookingChange={() => {
                    setActiveStep(CommonConstants.SELECT_SOLVENT_DIALOG);
                    setOpenForSubscribeDialog(true);
                    setOpenForSelectBookingChangeorDeleteDialog(false);
                }}
                booking={targetBooking}
                targetBookingSolvent={targetBookingSolvent}
            />
            <ConfirmDialog
                isOpen={isOpenForConfirmDialog}
                onClickCancel={() => {
                    setOpenForConfirmDialog(false);
                    setBookableSolvent([]);
                    setTargetBookingSolvent([]);
                }}
                booking={targetBooking}
            />
            <ConfirmModal
                isOpen={isOpenForConfirmModal}
                onClickNo={() => {
                    setOpenForConfirmModal(false);
                }}
                onClickYes={() => {
                    deleteBooking();
                }}
                message={messageForConfirmModal}
            />
            <Grid container>
                <Grid item xs={4}>
                    <Button
                        className={classes.infoButton}
                        variant="outlined"
                        startIcon={
                            <InfoOutlinedIcon
                                color={`action`}
                                fontSize={`small`}/>
                        }
                        onClick={() => {setOpenForSolventsInfoByBookingTypeDialog(true);}}>
                        予約できる溶媒の種類
                    </Button>
                </Grid>
                {isPcSize ? (
                    <Grid item xs={4} className={classes.cautions_select_pc}>
                        <span>※希望日を選択してください</span>
                    </Grid>
                ) : (
                    <Grid item xs={8} className={classes.cautions_select}>
                        <span>※希望日を選択してください</span>
                    </Grid>
                )}
            </Grid>
            <div className={classes.cautions}>
                <ul>
                    <li>午前の部 / 当日予約締切：{ConfigGetter.get(ConfigKey.BOOKING_DEADLINE_AM)}</li>
                    <li>午後の部 / 当日予約締切：{ConfigGetter.get(ConfigKey.BOOKING_DEADLINE_PM)}</li>
                    <li>
                        <a href={ConfigGetter.get(ConfigKey.WASTE_LINK)} target="_blank" rel="noopener noreferrer">
                            廃液回収オンライン予約リンク
                        </a>
                    </li>
                </ul>
            </div>
            <SolventsInfoByBookingTypeDialog
                isOpen={isOpenForSolventsInfoByBookingTypeDialog}
                onClickClose={() => {
                    setOpenForSolventsInfoByBookingTypeDialog(false);
                }}
            />
            <Loading isLoading={isLoading} />
            <p className="error">{errorMessage}</p>
            <FullCalendar
                locale={ConfigGetter.get(ConfigKey.CALENDAR_LOCALE)} // ロケール設定。
                plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin, listPlugin]} // 週表示、月表示、日付等のクリックを可能にするプラグインを設定。
                initialView={viewMode} // カレンダーの初期表示設定
                initialDate={initialDate} // カレンダーが最初に読み込まれたときに表示される最初の日付
                slotDuration={ConfigGetter.get(ConfigKey.CALENDAR_SLOTDURATION)} // 日表示した時の時間軸の単位。
                selectable={false} // 日付選択を可能にする。interactionPluginが有効になっている場合のみ。
                navLinks={false} // カレンダー内の日付クリックで日表示に遷移するかどう
                // ビジネス時間の設定。（0:日曜 〜 7:土曜）
                businessHours={[
                    {
                        // 午前
                        daysOfWeek: ConfigGetter.get(ConfigKey.CALENDAR_DAYS_OF_WEEK),
                        startTime: ConfigGetter.get(ConfigKey.CALENDAR_START_TIME_AM),
                        endTime: ConfigGetter.get(ConfigKey.CALENDAR_END_TIME_AM),
                    },
                    {
                        // 午後
                        daysOfWeek: ConfigGetter.get(ConfigKey.CALENDAR_DAYS_OF_WEEK),
                        startTime: ConfigGetter.get(ConfigKey.CALENDAR_START_TIME_PM),
                        endTime: ConfigGetter.get(ConfigKey.CALENDAR_END_TIME_PM),
                    },
                ]}
                weekends={true} // 週末を強調表示する。
                views={{
                    dayGridMonth: {
                        titleFormat: (obj: any) => {
                            return (
                                obj.start.year + ` / ` + (obj.start.month + 1) //+ `/` + obj.start.day + `-` + obj.end.day
                            );
                        },
                    },
                    timeGridDay: {
                        titleFormat: (obj: any) => {
                            return obj.date.year + ` / ` + (obj.date.month + 1) + ` / ` + obj.date.day;
                        },
                    },
                    listMonth: {
                        titleFormat: (obj: any) => {
                            return (
                                obj.start.year + ` / ` + (obj.start.month + 1) + ` / ` + obj.start.day + ` - ` + obj.end.day
                            );
                        },
                    },
                    listWeek: {
                        titleFormat: (obj: any) => {
                            return (
                                obj.start.year + ` / ` + (obj.start.month + 1) + ` / ` + obj.start.day + ` - ` +
                                (obj.start.year !== obj.end.year ? obj.end.year + ` / ` :  ``) +
                                (obj.start.month !== obj.end.month ? (obj.end.month + 1) + ` / ` :  ``) + obj.end.day
                            );
                        },
                    },
                }}
                titleFormat={{
                    // タイトルのフォーマット
                    year: `numeric`,
                    month: `short`,
                    day: `numeric`,
                    // weekday: `short`,
                }}
                listDayFormat={{
                    year: `numeric`,
                    month: `short`,
                    day: `numeric`,
                    weekday: `short`,
                }}
                slotLabelFormat={{
                    // 週、日表示の時間フォーマット
                    hour: `numeric`,
                    minute: `2-digit`,
                }}
                dayCellContent={(el: any) => {
                    el.dayNumberText = el.dayNumberText.replace(`日`, ``);
                }}
                listDaySideFormat={false}
                headerToolbar={{
                    // カレンダーのヘッダー設定
                    start: `prev,today,next`,
                    center: `title`,
                    end: `${CommonConstants.VIEWMODE_DAY_GRID_MONTH},${CommonConstants.VIEWMODE_LIST_WEEK}`,
                }}
                buttonText={{
                    today: `今週`,
                    month: `月`,
                    day: `日`,
                    list: `予約一覧`,
                }}
                slotEventOverlap={false} // イベントを重ねて表示するかどうか
                allDaySlot={false} // allDayを非表示にする
                stickyHeaderDates={true} // スクロール中に日付を固定する
                slotMinTime={ConfigGetter.get(ConfigKey.CALENDAR_START_TIME_AM)} // 週表示の開始時間
                slotMaxTime={ConfigGetter.get(ConfigKey.CALENDAR_END_TIME_PM)} // 週表示の終了時間
                eventTimeFormat={{
                    // イベントの時間表示方法の設定
                    hour: `2-digit`,
                    minute: `2-digit`,
                    meridiem: false,
                }}
                //eventDidMount={eventDidMount}
                contentHeight="auto"
                datesSet={(arg: DatesSetArg) => {
                    handleChangeDate(arg.start, arg.end);
                }}
                ref={ref}
                events={convertEventsForCalendar()}
                eventClick={handleCLick}
                eventContent={renderEventContent}
                viewDidMount={() => viewDidMount()}
                dayCellClassNames={handleDayCellClassNames}
                // 空白押下時の処理
                dateClick={handleDateClick}
                // 日付押下時の処理
                navLinkDayClick={handleDateClick}
            />
        </div>
    );
};
